/**
 * Where an "@" stops being a mention and starts being prose.
 *
 * A product name can contain spaces, so the text after an "@" cannot simply
 * stop at the first one — and the first version therefore ran to the end of
 * the box. Paste a prompt with an "@" in the middle of it and the entire rest
 * of the sentence became the product search.
 *
 * Two bounds fix that, and neither is a character limit:
 *
 *   the caret — only text BEFORE it is being typed;
 *   meaning   — while the query names nothing, it is not a mention.
 *
 * Pure, and apart from the panel, because this is exactly the kind of logic
 * that goes wrong quietly inside a component nobody can test.
 */

/** How the query is bounded. */
export interface MentionContext {
    /** Does this query still name something choosable? */
    matches: (query: string) => boolean;
    /** Names already accepted — text past one of these is settled. */
    accepted: string[];
}

/**
 * The product query the caret is sitting in, or null if there isn't one.
 *
 * Returns '' (not null) directly after a bare "@", which is what opens the
 * full list.
 */
export function mentionQueryAt(
    value: string,
    caret: number,
    ctx: MentionContext,
): string | null {
    const head = value.slice(0, Math.max(0, Math.min(caret, value.length)));
    const match = /(?:^|\s)@([^@]*)$/.exec(head);
    if (!match) return null;
    const query = match[1];
    if (query === '') return '';
    // A name already accepted, followed by a space, is settled text — the
    // vendor has moved on and is writing the rest of the sentence.
    if (ctx.accepted.some((name) => name && query.startsWith(`${name} `))) return null;
    return ctx.matches(query) ? query : null;
}

export interface MentionInsertion {
    /** The whole box after the mention is written in. */
    value: string;
    /** Where the caret belongs: past the mention and its trailing space. */
    caret: number;
}

/**
 * Write a chosen name into the box at the caret.
 *
 * The trailing space is not a nicety - without the caret being moved past it,
 * the next keystroke lands inside the name that was just chosen.
 *
 * Still used by the Ask-AI panel, which is a plain textarea. The Content Studio
 * composer writes an element instead and does not come through here.
 */
export function insertMention(value: string, caret: number, name: string): MentionInsertion {
    const at = Math.max(0, Math.min(caret, value.length));
    const head = value.slice(0, at);
    const tail = value.slice(at);
    const written = /(^|\s)@([^@]*)$/.test(head)
        ? head.replace(/(^|\s)@([^@]*)$/, `$1@${name} `)
        : `${head}${head && !head.endsWith(' ') ? ' ' : ''}@${name} `;
    return { value: written + tail, caret: written.length };
}

/**
 * Names present in the text as "@Name".
 *
 * Longest first, so a product whose name begins another cannot shadow it.
 */
export function mentionsPresent(value: string, names: string[]): string[] {
    if (!value.includes('@')) return [];
    return [...names]
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
        .filter((name) => value.includes(`@${name}`));
}
